import { Link } from "react-router-dom";
import {
  FileCheck2,
  BookOpen,
  Landmark,
  Globe2,
  Briefcase,
  Building2,
  ArrowRight,
  Check,
  ShieldCheck,
  Award,
  Clock,
  Target,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import Reveal from "@/components/site/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "How quickly will I get a response after I enquire?",
    a: "Within one business day. For urgent compliance matters (notices, near-deadline filings), we often respond the same day.",
  },
  {
    q: "Do you work with businesses outside Surat and Gujarat?",
    a: "Yes. The majority of our work is paperless and delivered over video, email and WhatsApp. We serve clients across India and also support Indian-owned businesses operating in the US.",
  },
  {
    q: "How do you charge — monthly retainer or per engagement?",
    a: "Both models exist. Ongoing compliance (GST, accounting, payroll) typically works best on a monthly retainer. One-off engagements like loan files, registrations and advisory projects are priced per scope.",
  },
  {
    q: "Will I get a single point of contact?",
    a: "Yes. Every client has a named relationship partner who owns the engagement end-to-end, supported by specialists across tax, accounting and finance.",
  },
  {
    q: "Can you help if my books are currently a mess?",
    a: "That is one of the most common onboarding scenarios. We start with a cleanup phase — bringing books up to date — and then move to a monthly rhythm once the base is stable.",
  },
  {
    q: "Is my data kept confidential?",
    a: "Yes. Client data is handled under strict confidentiality. We use access-controlled storage, signed engagement letters and named approvers for any external sharing.",
  },
];

const services = [
  {
    num: "01",
    icon: FileCheck2,
    title: "Tax & Compliance",
    desc: "End-to-end direct and indirect tax compliance for businesses of every size — handled with precision, on time.",
    items: [
      "GST Registration & Filing",
      "Income Tax (ITR) Filing",
      "TDS Return Filing",
      "ROC Compliance",
    ],
  },
  {
    num: "02",
    icon: BookOpen,
    title: "Accounting & Bookkeeping",
    desc: "Decision-ready books and clear reporting that tell you exactly where the business stands — every month.",
    items: [
      "Bookkeeping & Accounting",
      "Financial Statements",
      "Reconciliation",
      "MIS Reports",
    ],
  },
  {
    num: "03",
    icon: Landmark,
    title: "Project Finance & Loan Assistance",
    desc: "Structured project reports and documentation that lenders trust — from CMA to disbursement follow-through.",
    items: [
      "Project Reports",
      "CMA Data",
      "Bank Loan Assistance",
      "Subsidy & Government Schemes",
    ],
  },
  {
    num: "04",
    icon: Globe2,
    title: "International Services",
    desc: "Cross-border accounting and tax support delivered with clarity, confidentiality and on-time reporting.",
    items: [
      "US Accounting",
      "US Taxation Support",
      "Payroll Processing",
      "Financial Reporting",
    ],
  },
  {
    num: "05",
    icon: Briefcase,
    title: "Business Advisory",
    desc: "Practical advisory to optimize cost, sharpen strategy and unlock the next phase of growth — with numbers to back it.",
    items: [
      "Business Planning",
      "Financial Analysis",
      "Cost Optimization",
      "Growth Strategies",
    ],
  },
  {
    num: "06",
    icon: Building2,
    title: "Business Registration & Compliance",
    desc: "From firm formation to digital signatures — set up right and stay compliant from day one.",
    items: [
      "Partnership / Firm Registration",
      "Company Registration",
      "Digital Signature (DSC)",
      "PAN / TAN / IEC Services",
    ],
  },
];

const commitments = [
  { icon: Award, title: "Expert Guidance", desc: "From experienced professionals." },
  { icon: ShieldCheck, title: "Trusted & Reliable", desc: "Confidentiality and accuracy assured." },
  { icon: Clock, title: "Timely & Efficient", desc: "On-time service, every time." },
  { icon: Target, title: "Outcome Driven", desc: "We don't just file — we add value." },
];

export default function Services() {
  return (
    <>
      {/* HEADER */}
      <section data-testid="services-header" className="page-offset bg-[#F8F9FA] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(10,31,68,0.08) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
            Our Services
          </span>
          <h1 className="mt-6 font-display font-light text-[#0A1F44] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            One-stop solutions <span className="italic">for all your business needs.</span>
          </h1>
          <div className="mt-8 h-[2px] w-14 bg-[#D4AF37] mx-auto" />
          <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
            Professional financial, tax and business advisory services that let you focus on what
            you do best — we take care of the rest.
          </p>
        </div>
      </section>

      {/* GRID - Premium numbered cards */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${i}`}
                style={{ animationDelay: `${i * 80}ms` }}
                className="reveal group relative bg-white border border-gray-200 p-8 md:p-10 flex flex-col overflow-hidden transition-all duration-500 hover:border-[#D4AF37] hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(10,31,68,0.25)]"
              >
                {/* Top accent bar */}
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-0 h-[3px] w-0 bg-[#D4AF37] transition-all duration-500 group-hover:w-full"
                />
                {/* Corner ornament */}
                <span
                  aria-hidden="true"
                  className="absolute -top-14 -right-14 w-32 h-32 rounded-full bg-[#0A1F44]/[0.03] group-hover:bg-[#D4AF37]/10 transition-colors duration-500"
                />

                {/* Number + Icon */}
                <div className="relative flex items-start justify-between">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A1F44] transition-colors duration-500">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-4xl md:text-5xl font-light text-[#0A1F44]/10 group-hover:text-[#D4AF37]/80 transition-colors duration-500 tracking-tight">
                    {s.num}
                  </span>
                </div>

                <h3 className="relative mt-8 font-display text-xl md:text-2xl text-[#0A1F44] font-medium">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-[14px] text-[#4B5563] leading-relaxed">{s.desc}</p>

                <div className="relative mt-6 h-px w-10 bg-[#D4AF37]" />
                <ul className="relative mt-6 space-y-3">
                  {s.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-3 text-[14px] text-[#111827] transition-transform duration-300 group-hover:translate-x-1"
                    >
                      <span className="mt-[7px] w-1.5 h-1.5 bg-[#D4AF37] shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`service-card-cta-${i}`}
                  className="relative mt-10 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#0A1F44] group-hover:text-[#D4AF37] transition-colors"
                >
                  Discuss this service
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* COMMITMENT / ASSURANCE STRIP */}
      <section data-testid="services-commitment" className="py-20 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Our Commitment"
            title="Four guarantees across every engagement."
            align="center"
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
            {commitments.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`commitment-${i}`}
                  className="bg-white border border-gray-200 p-6 md:p-8 card-hover group text-center"
                >
                  <div className="mx-auto w-12 h-12 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A1F44] transition-colors">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h4 className="mt-5 font-display text-[15px] md:text-base text-[#0A1F44] font-semibold uppercase tracking-[0.14em]">
                    {c.title}
                  </h4>
                  <p className="mt-2 text-[13px] text-[#4B5563] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="services-faq" className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="Frequently Asked"
              title="Answers to common questions."
              align="center"
            />
          </Reveal>
          <Reveal delay={120}>
            <Accordion
              type="single"
              collapsible
              className="mt-14 divide-y divide-gray-200 border-y border-gray-200"
              data-testid="faq-accordion"
            >
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-0">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="py-6 text-left text-[15px] md:text-[17px] font-display text-[#0A1F44] font-medium hover:no-underline hover:text-[#D4AF37] transition-colors"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${i}`}
                    className="pb-6 text-[14px] md:text-[15px] text-[#4B5563] leading-relaxed"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* TAGLINE STRIP */}
      <section className="py-20 md:py-24 bg-[#0A1F44] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
            Our Promise
          </span>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
            We don't just do the numbers —{" "}
            <span className="text-[#D4AF37]">we add value to your business.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto text-[15px] md:text-base leading-relaxed">
            Our commitment is your success. Structured, reliable, and built for the long run.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A1F44] px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-white transition-colors"
            >
              Get Consultation <ArrowRight size={18} />
            </Link>
            <a
              href="https://wa.me/917487978020?text=Hi%2C%20I%27d%20like%20a%20consultation%20from%20Avvanta%20Advisory"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
